import { BetDetails, ParlaySelection } from "../models/MyBetModel.tsx";
import { ClosingLineAnalysisDoc, ClosingLineBetItem, ClosingLineLineType, ClosingLineMarketKind } from "../models/ClosingLineAnalysis.tsx";
import { Odd } from "../models/Odd_response_model.tsx";
import { getBetResultDelta, isFreePlayBet } from "./functions.tsx";

/** Analytics / Closing Line is available in admin. */
export const CLOSING_LINE_ANALYTICS_ENABLED = true;

export type LineKind = ClosingLineMarketKind;

export type ClosingLinePlayerRow = Pick<
    ClosingLineAnalysisDoc,
    | "userName"
    | "points"
    | "price"
    | "pointsDisplay"
    | "priceDisplay"
    | "beatLine"
    | "totalBets"
    | "percentage"
    | "percentageDisplay"
    | "winLoss"
> & {
    displayName?: string;
    pointsSampleCount: number;
    priceSampleCount: number;
};

export type ClosingLineBetRow = {
    key: string;
    ticket: string;
    userName: string;
    displayName: string;
    betDate?: Date;
    description: string;
    marketLabel: string;
    kind: LineKind;
    takenLabel: string;
    closingLabel: string;
    valueLabel: string;
    value: number | null;
    beatLine: boolean;
    winLoss: number;
};

type ClosingSnapshot = {
    price?: number | null;
    points?: number | null;
};

type AnalyzableLine = {
    key: string;
    ticket: string;
    userName: string;
    displayName: string;
    betDate?: Date;
    description: string;
    marketLabel: string;
    kind: LineKind;
    totalSide: "over" | "under" | null;
    takenLabel: string;
    closingLabel: string;
    centsValue: number | null;
    pointsValue: number | null;
    beatLine: boolean;
    ticketWinLoss: number;
};

type BetWithClosing = BetDetails & {
    closingOdd?: ClosingSnapshot;
    closingLine?: ClosingSnapshot;
    closingPrice?: number | null;
    closingPoints?: number | null;
};

const DELETED_STATUSES = new Set(["deleted", "Deleted"]);

const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
};

const marketText = (marketId?: string | null, marketName?: string | null): string =>
    `${marketId ?? ""} ${marketName ?? ""}`.toLowerCase().replace(/_/g, " ").trim();

export const getLineKind = (marketId?: string | null, marketName?: string | null): LineKind | null => {
    const market = marketText(marketId, marketName);
    if (!market) return null;
    if (market.includes("alternate") || market.includes("player") || market.includes("batter") || market.includes("pitcher")) {
        return null;
    }
    if (market.includes("moneyline") || market.includes("money line") || market === "ml") {
        return "moneyline";
    }
    if (
        market.includes("point spread") ||
        market.includes("run line") ||
        market.includes("puck line") ||
        /(^|\s)spreads?(\s|$)/.test(market)
    ) {
        return "spread";
    }
    if (market.includes("team total") || market.includes("total")) {
        return "total";
    }
    return null;
};

const getTotalSide = (odd?: Pick<Odd, "name" | "selection" | "normalized_selection" | "selection_line"> | null): "over" | "under" | null => {
    const text = [
        odd?.selection_line,
        odd?.normalized_selection,
        odd?.selection,
        odd?.name,
    ].filter(Boolean).join(" ").toLowerCase();
    if (text.includes("under")) return "under";
    if (text.includes("over")) return "over";
    return null;
};

/**
 * Moneyline CLV in cents: taken American minus closing American.
 * Took -150, closed -200 → +50¢. Took +130, closed +100 → +30¢.
 */
export const beatLineCents = (takenAmerican: number, closingAmerican: number): number =>
    takenAmerican - closingAmerican;

/**
 * Spread CLV in points: taken points minus closing points.
 * Took +2.5, closed +1.0 → +1.5 pts. Took -10, closed -14 → +4.0 pts.
 */
export const beatLineSpreadPoints = (takenPoints: number, closingPoints: number): number =>
    takenPoints - closingPoints;

/**
 * Total CLV in points. Over benefits from a higher number; Under from a lower one.
 * Over 20.5 → 12.5 = +8.0 pts. Under 8.5 → 11.5 = +3.0 pts.
 */
export const beatLineTotalPoints = (
    takenPoints: number,
    closingPoints: number,
    side: "over" | "under",
): number =>
    side === "over" ? takenPoints - closingPoints : closingPoints - takenPoints;

export const formatSignedCents = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) return "—";
    const rounded = Math.round(value);
    const sign = rounded > 0 ? "+" : "";
    return `${sign}${rounded}¢`;
};

export const formatSignedPoints = (value: number | null | undefined): string => {
    if (value == null || Number.isNaN(value)) return "—";
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toFixed(1)} pts`;
};

export const formatAmericanOdds = (price: number | null | undefined): string => {
    if (price == null || Number.isNaN(price)) return "—";
    return price > 0 ? `+${price}` : `${price}`;
};

export const formatSpreadPoints = (points: number | null | undefined): string => {
    if (points == null || Number.isNaN(points)) return "—";
    return points > 0 ? `+${points}` : `${points}`;
};

/**
 * Taken or closing line for Original Bet / Final Bet.
 * Moneyline: +130. Spread: +7.5 (-130). Total: 20.5 (-110).
 */
export const formatOriginalFinalBetLine = (
    kind: ClosingLineMarketKind,
    points: number | null | undefined,
    price: number | null | undefined,
): string => {
    if (kind === "moneyline") {
        const odds = formatAmericanOdds(price);
        return odds === "—" ? "" : odds;
    }
    const line = formatSpreadPoints(points);
    const juice = formatAmericanOdds(price);
    if (line !== "—" && juice !== "—") return `${line} (${juice})`;
    if (line !== "—") return line;
    if (juice !== "—") return juice;
    return "";
};

export const originalBetLabel = (bet: ClosingLineBetItem): string =>
    bet.originalBet?.trim() || bet.taken || "—";

export const finalBetLabel = (bet: ClosingLineBetItem): string =>
    bet.finalBet?.trim() || bet.closing || "—";

export const signedClassName = (value: number | null | undefined): string => {
    if (value == null || value === 0) return "";
    return value > 0 ? "positive-admin" : "negative-admin";
};

const displayOrFallback = (display: string | undefined, fallback: string): string =>
    display?.trim() ? display : fallback;

export const closingLinePointsLabel = (row: Pick<ClosingLinePlayerRow, "points" | "pointsDisplay">): string =>
    displayOrFallback(row.pointsDisplay, formatSignedPoints(row.points));

export const closingLinePriceLabel = (row: Pick<ClosingLinePlayerRow, "price" | "priceDisplay">): string =>
    displayOrFallback(row.priceDisplay, formatSignedCents(row.price));

export const closingLinePercentageLabel = (
    row: Pick<ClosingLinePlayerRow, "percentage" | "percentageDisplay">,
): string =>
    displayOrFallback(row.percentageDisplay, `${row.percentage.toFixed(2)}%`);

export const lineTypeForKind = (kind: LineKind): ClosingLineLineType =>
    kind === "moneyline" ? "price" : "points";

export const betsForLineType = (
    bets: ClosingLineBetItem[] | undefined,
    lineType: ClosingLineLineType,
): ClosingLineBetItem[] =>
    (bets ?? []).filter((bet) => {
        if (bet.lineType) return bet.lineType === lineType;
        if (bet.kind) return lineTypeForKind(bet.kind) === lineType;
        return true;
    });

export const closingLineDeltaLabel = (
    value: number | null | undefined,
    kind: LineKind,
    display?: string,
): string => {
    if (display?.trim()) return display.replace(/¢| pts/gi, "").trim();
    if (value == null || Number.isNaN(value)) return "";
    const body = kind === "moneyline"
        ? (Number.isInteger(value) ? String(value) : value.toFixed(2))
        : value.toFixed(1);
    return value > 0 ? `+${body}` : body;
};

export const closingLineCombinedLabel = (bet: ClosingLineBetItem): string => {
    const closing = (bet.closing || "").trim() || "—";
    if (closing.includes("(")) return closing;
    const delta = closingLineDeltaLabel(bet.value, bet.kind, bet.valueDisplay);
    return delta ? `${closing} (${delta})` : closing;
};

export const toClosingLineBetDate = (bet: ClosingLineBetItem): Date | undefined => {
    const timestamp = bet.acceptedAt ?? bet.betDate;
    if (!timestamp) return undefined;
    const stamp = timestamp as { toDate?: () => Date; seconds?: number; _seconds?: number };
    if (typeof stamp.toDate === "function") return stamp.toDate();
    const seconds = stamp.seconds ?? stamp._seconds;
    if (typeof seconds === "number") return new Date(seconds * 1000);
    return undefined;
};

const mean = (values: number[]): number | null => {
    if (values.length === 0) return null;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const toBetDate = (bet: BetDetails): Date | undefined => {
    const timestamp = bet.betDate;
    if (!timestamp) return undefined;
    if (typeof timestamp.toDate === "function") return timestamp.toDate();
    if (typeof timestamp.seconds === "number") return new Date(timestamp.seconds * 1000);
    return undefined;
};

const snapshotFromUnknown = (value: unknown): ClosingSnapshot | null => {
    if (!value || typeof value !== "object") return null;
    const record = value as ClosingSnapshot;
    const price = toFiniteNumber(record.price);
    const points = toFiniteNumber(record.points);
    if (price == null && points == null) return null;
    return { price, points };
};

const getClosingSnapshot = (bet: BetWithClosing, odd?: Odd | null): ClosingSnapshot | null => {
    const fromOdd = snapshotFromUnknown(bet.closingOdd);
    if (fromOdd) return fromOdd;
    const fromLine = snapshotFromUnknown(bet.closingLine);
    if (fromLine) return fromLine;
    if (bet.closingPrice != null || bet.closingPoints != null) {
        return {
            price: toFiniteNumber(bet.closingPrice),
            points: toFiniteNumber(bet.closingPoints),
        };
    }

    if (!odd || !Array.isArray(bet.odds) || bet.odds.length === 0) return null;

    const later = [...bet.odds]
        .filter((candidate) => {
            const sameSelection =
                candidate.id === odd.id ||
                (
                    candidate.market_id === odd.market_id &&
                    candidate.selection === odd.selection &&
                    (candidate.team_id || "") === (odd.team_id || "")
                );
            const laterStamp = (candidate.timestamp ?? 0) - (odd.timestamp ?? 0) >= 60;
            return sameSelection && laterStamp;
        })
        .sort((a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0))[0];

    if (!later) return null;
    return {
        price: toFiniteNumber(later.price),
        points: toFiniteNumber(later.points),
    };
};

const describeSelection = (odd?: Odd | null, fallbackName?: string): string =>
    (odd?.name || fallbackName || "").trim() || "—";

const marketLabelFor = (odd?: Odd | null, marketName?: string): string =>
    (odd?.market || marketName || odd?.market_id || "").trim() || "—";

const formatTotalLine = (points: number | null, side: "over" | "under" | null): string => {
    if (points == null) return "—";
    if (side === "under") return `Under ${points}`;
    if (side === "over") return `Over ${points}`;
    return `${points}`;
};

const takenLabelFor = (kind: LineKind, odd?: Odd | null): string => {
    if (kind === "moneyline") return formatAmericanOdds(toFiniteNumber(odd?.price));
    if (kind === "spread") return formatSpreadPoints(toFiniteNumber(odd?.points));
    return formatTotalLine(toFiniteNumber(odd?.points), getTotalSide(odd));
};

const closingLabelFor = (
    kind: LineKind,
    closing?: ClosingSnapshot | null,
    side?: "over" | "under" | null,
): string => {
    if (!closing) return "—";
    if (kind === "moneyline") return formatAmericanOdds(toFiniteNumber(closing.price));
    if (kind === "spread") return formatSpreadPoints(toFiniteNumber(closing.points));
    return formatTotalLine(toFiniteNumber(closing.points), side ?? null);
};

const lineValue = (
    kind: LineKind,
    odd?: Odd | null,
    closing?: ClosingSnapshot | null,
): { centsValue: number | null; pointsValue: number | null } => {
    if (!closing) return { centsValue: null, pointsValue: null };

    if (kind === "moneyline") {
        const taken = toFiniteNumber(odd?.price);
        const closed = toFiniteNumber(closing.price);
        if (taken == null || closed == null) return { centsValue: null, pointsValue: null };
        return { centsValue: beatLineCents(taken, closed), pointsValue: null };
    }

    const takenPoints = toFiniteNumber(odd?.points);
    const closedPoints = toFiniteNumber(closing.points);
    if (takenPoints == null || closedPoints == null) return { centsValue: null, pointsValue: null };

    if (kind === "spread") {
        return { centsValue: null, pointsValue: beatLineSpreadPoints(takenPoints, closedPoints) };
    }

    const side = getTotalSide(odd);
    if (!side) return { centsValue: null, pointsValue: null };
    return { centsValue: null, pointsValue: beatLineTotalPoints(takenPoints, closedPoints, side) };
};

const isAnalyzableTicket = (bet: BetDetails): boolean => {
    if (isFreePlayBet(bet)) return false;
    const status = String(bet.betStatus ?? bet.status ?? "");
    if (DELETED_STATUSES.has(status)) return false;
    return Boolean(bet.player?.userName);
};

const buildLine = (
    bet: BetDetails,
    odd: Odd | undefined,
    marketName: string | undefined,
    betName: string | undefined,
    key: string,
    ticketWinLoss: number,
    closingOverride?: ClosingSnapshot | null,
): AnalyzableLine | null => {
    const kind = getLineKind(odd?.market_id, odd?.market || marketName);
    if (!kind) return null;

    const closing = closingOverride ?? getClosingSnapshot(bet as BetWithClosing, odd);
    const { centsValue, pointsValue } = lineValue(kind, odd, closing);
    const value = kind === "moneyline" ? centsValue : pointsValue;
    const totalSide = kind === "total" ? getTotalSide(odd) : null;

    return {
        key,
        ticket: bet.ticket || key,
        userName: bet.player?.userName || "",
        displayName: bet.player?.name || bet.player?.parent || "",
        betDate: toBetDate(bet),
        description: describeSelection(odd, betName),
        marketLabel: marketLabelFor(odd, marketName),
        kind,
        totalSide,
        takenLabel: takenLabelFor(kind, odd),
        closingLabel: closingLabelFor(kind, closing, totalSide),
        centsValue,
        pointsValue,
        beatLine: value != null && value > 0,
        ticketWinLoss,
    };
};

const collectLines = (bets: BetDetails[]): AnalyzableLine[] => {
    const lines: AnalyzableLine[] = [];

    bets.forEach((bet, betIndex) => {
        if (!isAnalyzableTicket(bet)) return;
        const ticketWinLoss = getBetResultDelta(bet);
        const ticket = bet.ticket || `bet-${betIndex}`;

        if (Array.isArray(bet.selections) && bet.selections.length > 0) {
            bet.selections.forEach((selection: ParlaySelection, legIndex) => {
                const line = buildLine(
                    bet,
                    selection.odd,
                    selection.odd?.market || selection.marketName,
                    selection.betName || selection.odd?.name,
                    `${ticket}-leg-${legIndex}`,
                    ticketWinLoss,
                    getClosingSnapshot(selection as BetWithClosing, selection.odd),
                );
                if (line) lines.push(line);
            });
            return;
        }

        const line = buildLine(
            bet,
            bet.odd,
            bet.odd?.market || bet.marketName,
            bet.betName || bet.odd?.name,
            ticket,
            ticketWinLoss,
        );
        if (line) lines.push(line);
    });

    return lines;
};

const toBetRow = (line: AnalyzableLine): ClosingLineBetRow => {
    const value = line.kind === "moneyline" ? line.centsValue : line.pointsValue;
    return {
        key: line.key,
        ticket: line.ticket,
        userName: line.userName,
        displayName: line.displayName,
        betDate: line.betDate,
        description: line.description,
        marketLabel: line.marketLabel,
        kind: line.kind,
        takenLabel: line.takenLabel,
        closingLabel: line.closingLabel,
        valueLabel: line.kind === "moneyline"
            ? formatSignedCents(line.centsValue)
            : formatSignedPoints(line.pointsValue),
        value,
        beatLine: line.beatLine,
        winLoss: line.ticketWinLoss,
    };
};

export const toClosingLinePlayerRow = (doc: ClosingLineAnalysisDoc): ClosingLinePlayerRow => ({
    userName: doc.userName,
    displayName: "",
    points: doc.points,
    price: doc.price,
    pointsDisplay: doc.pointsDisplay,
    priceDisplay: doc.priceDisplay,
    beatLine: doc.beatLine,
    totalBets: doc.totalBets,
    percentage: doc.percentage,
    percentageDisplay: doc.percentageDisplay,
    winLoss: doc.winLoss,
    pointsSampleCount: 0,
    priceSampleCount: 0,
});

export const buildClosingLinePlayerRows = (
    bets: BetDetails[],
    nameByUser: Record<string, string> = {},
): ClosingLinePlayerRow[] => {
    const lines = collectLines(bets);
    const grouped = new Map<string, AnalyzableLine[]>();

    lines.forEach((line) => {
        const current = grouped.get(line.userName) ?? [];
        current.push(line);
        grouped.set(line.userName, current);
    });

    return [...grouped.entries()]
        .map(([userName, playerLines]) => {
            const pointValues = playerLines
                .map((line) => line.pointsValue)
                .filter((value): value is number => value != null);
            const centValues = playerLines
                .map((line) => line.centsValue)
                .filter((value): value is number => value != null);
            const totalBets = playerLines.length;
            const beatLine = playerLines.filter((line) => line.beatLine).length;
            const seenTickets = new Set<string>();
            const winLoss = playerLines.reduce((sum, line) => {
                if (seenTickets.has(line.ticket)) return sum;
                seenTickets.add(line.ticket);
                return sum + line.ticketWinLoss;
            }, 0);
            const points = mean(pointValues);
            const price = mean(centValues);
            const percentage = totalBets > 0 ? (beatLine / totalBets) * 100 : 0;

            return {
                userName,
                displayName: nameByUser[userName] || playerLines[0]?.displayName || "",
                points,
                price,
                pointsDisplay: formatSignedPoints(points),
                priceDisplay: formatSignedCents(price),
                pointsSampleCount: pointValues.length,
                priceSampleCount: centValues.length,
                beatLine,
                totalBets,
                percentage,
                percentageDisplay: `${percentage.toFixed(2)}%`,
                winLoss,
            };
        })
        .sort((a, b) => a.userName.localeCompare(b.userName, undefined, { numeric: true, sensitivity: "base" }));
};

export const buildClosingLineBetRows = (
    bets: BetDetails[],
    userName?: string,
): ClosingLineBetRow[] =>
    collectLines(bets)
        .filter((line) => !userName || line.userName === userName)
        .map(toBetRow)
        .sort((a, b) => (b.betDate?.getTime() ?? 0) - (a.betDate?.getTime() ?? 0));

export const summarizeClosingLineRows = (rows: ClosingLinePlayerRow[]): ClosingLinePlayerRow => {
    const totalBets = rows.reduce((sum, row) => sum + row.totalBets, 0);
    const beatLine = rows.reduce((sum, row) => sum + row.beatLine, 0);
    const pointsSampleCount = rows.reduce((sum, row) => sum + (row.pointsSampleCount ?? 0), 0);
    const priceSampleCount = rows.reduce((sum, row) => sum + (row.priceSampleCount ?? 0), 0);
    const hasSampleCounts = pointsSampleCount > 0 || priceSampleCount > 0;
    const pointsTotal = rows.reduce((sum, row) => (
        row.points != null ? sum + row.points * (hasSampleCounts ? row.pointsSampleCount || 0 : 1) : sum
    ), 0);
    const priceTotal = rows.reduce((sum, row) => (
        row.price != null ? sum + row.price * (hasSampleCounts ? row.priceSampleCount || 0 : 1) : sum
    ), 0);
    const pointsWeight = hasSampleCounts
        ? pointsSampleCount
        : rows.filter((row) => row.points != null).length;
    const priceWeight = hasSampleCounts
        ? priceSampleCount
        : rows.filter((row) => row.price != null).length;
    const points = pointsWeight > 0 ? pointsTotal / pointsWeight : null;
    const price = priceWeight > 0 ? priceTotal / priceWeight : null;
    const percentage = totalBets > 0 ? (beatLine / totalBets) * 100 : 0;

    return {
        userName: "Total",
        displayName: "",
        points,
        price,
        pointsDisplay: formatSignedPoints(points),
        priceDisplay: formatSignedCents(price),
        pointsSampleCount,
        priceSampleCount,
        beatLine,
        totalBets,
        percentage,
        percentageDisplay: `${percentage.toFixed(2)}%`,
        winLoss: rows.reduce((sum, row) => sum + row.winLoss, 0),
    };
};
